import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getBaseUrl } from '../config/app-config';
import { supabase } from './supabase';
import { analytics } from './analytics';
import { setAdminAccessTokenForApi } from './db-storage';
import { needsMfaStep, pickFactorForChallenge } from './mfa-utils';
import { useAppSettings } from './app-settings-context';
import { CLOUD_BOOTSTRAP_DONE_KEY } from './cloud-sync';
import { trackTelemetryEvent } from './telemetry';
import { clearAllConsumerLoginSnapshots, clearLoginSnapshotSessionState } from './consumer-login-snapshot';

/** Verbose auth tracing — development only (keeps production consoles readable). */
const authDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.log(...args);
};

export interface User {
  id: string;
  email: string;
  user_metadata?: Record<string, string>;
}

export type MfaChallengeState = {
  factorId: string;
  factorType: 'totp' | 'webauthn';
};

/** Ensure user_metadata only has string values so it can never cause React #130 when rendered */
function safeUserMetadata(m: unknown): Record<string, string> {
  if (!m || typeof m !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(m)) {
    if (typeof v === 'string') out[k] = v;
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
  }
  return out;
}

/** Normalize to a User shape that is safe to render (no nested objects in user_metadata) */
function normalizeUser(u: { id?: string; email?: string; user_metadata?: unknown } | null): User | null {
  if (!u || typeof u.id !== 'string') return null;
  return {
    id: u.id,
    email: typeof u.email === 'string' ? u.email : '',
    user_metadata: safeUserMetadata(u.user_metadata),
  };
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures in restricted browser contexts.
  }
}

function safeLocalStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage removal failures in restricted browser contexts.
  }
}

function persistUserSession(authUser: User | null, token: string | null) {
  if (authUser && token) {
    safeLocalStorageSet('tension_access_token', token);
    safeLocalStorageSet('tension_user', JSON.stringify(authUser));
  } else {
    safeLocalStorageRemove('tension_access_token');
    safeLocalStorageRemove('tension_user');
  }
}

async function applySessionFromSupabase(
  setUser: (u: User | null) => void,
  setAccessToken: (t: string | null) => void,
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.access_token) {
    const authUser = normalizeUser(session.user);
    setUser(authUser);
    setAccessToken(session.access_token);
    persistUserSession(authUser, session.access_token);
  }
}

/** Reject if `promise` does not settle within `ms` (prevents indefinite hangs on setSession / MFA APIs). */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  });
}

const SET_SESSION_TIMEOUT_MS = 15_000;
const MFA_API_TIMEOUT_MS = 8_000;

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  loading: boolean;
  mfaChallenge: MfaChallengeState | null;
  signUp: (email: string, password: string, name: string) => Promise<{ mfaPending: boolean }>;
  signIn: (email: string, password: string) => Promise<{ mfaPending: boolean }>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  completeMfaTotp: (code: string) => Promise<void>;
  completeMfaPasskey: () => Promise<void>;
  cancelMfaChallenge: () => Promise<void>;
  /** After OAuth redirect: returns true if user must complete MFA on /auth */
  syncMfaFromSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  accessToken: null,
  loading: true,
  mfaChallenge: null,
  signUp: async () => ({ mfaPending: false }),
  signIn: async () => ({ mfaPending: false }),
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  signOut: async () => {},
  completeMfaTotp: async () => {},
  completeMfaPasskey: async () => {},
  cancelMfaChallenge: async () => {},
  syncMfaFromSession: async () => false,
});

type SignInApiResponse = {
  error?: string;
  session?: { access_token: string; refresh_token: string };
  user?: unknown;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const { mfaEnforcedAtLogin } = useAppSettings();
  const mfaEnforcedRef = useRef(mfaEnforcedAtLogin);
  mfaEnforcedRef.current = mfaEnforcedAtLogin;

  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallengeState | null>(null);

  useEffect(() => {
    if (!mfaEnforcedAtLogin) {
      setMfaChallenge(null);
    }
  }, [mfaEnforcedAtLogin]);

  useEffect(() => {
    analytics.setAuthToken(accessToken);
  }, [accessToken]);

  const syncMfaFromSession = useCallback(async (): Promise<boolean> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token || !session.refresh_token) {
      setMfaChallenge(null);
      return false;
    }
    await withTimeout(
      supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }),
      SET_SESSION_TIMEOUT_MS,
      'Saving your session timed out. Please try again.',
    );
    if (!mfaEnforcedAtLogin) {
      setMfaChallenge(null);
      return false;
    }
    const mfa = supabase.auth.mfa;
    const need = await withTimeout(
      needsMfaStep(mfa),
      MFA_API_TIMEOUT_MS,
      'Sign-in security check timed out. Please try again.',
    );
    if (!need) {
      setMfaChallenge(null);
      return false;
    }
    const { data: fac, error } = await withTimeout(
      mfa.listFactors(),
      MFA_API_TIMEOUT_MS,
      'Sign-in security check timed out. Please try again.',
    );
    if (error) console.warn('MFA listFactors:', error);
    const pick = pickFactorForChallenge(fac);
    if (!pick) {
      setMfaChallenge(null);
      return false;
    }
    setMfaChallenge({ factorId: pick.id, factorType: pick.factorType });
    return true;
  }, [mfaEnforcedAtLogin]);

  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ Auth loading timeout - forcing loading=false');
      setLoading(false);
    }, 3000);

    const initializeAuth = async () => {
      try {
        authDebug('🔐 Initializing auth...');
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Session error:', error);
          safeLocalStorageRemove('tension_access_token');
          safeLocalStorageRemove('tension_user');
          setUser(null);
          setAccessToken(null);
          setMfaChallenge(null);
        } else if (data.session) {
          try {
            await withTimeout(
              supabase.auth.setSession({
                access_token: data.session.access_token,
                refresh_token: data.session.refresh_token,
              }),
              SET_SESSION_TIMEOUT_MS,
              'init setSession timeout',
            );
          } catch (e) {
            console.warn('Initial setSession timed out or failed; continuing with stored session:', e);
          }
          const authUser = normalizeUser(data.session.user);
          if (mfaEnforcedRef.current) {
            try {
              const mfa = supabase.auth.mfa;
              const need = await withTimeout(
                needsMfaStep(mfa),
                MFA_API_TIMEOUT_MS,
                'init MFA check timeout',
              );
              if (need) {
                const { data: fac } = await withTimeout(
                  mfa.listFactors(),
                  MFA_API_TIMEOUT_MS,
                  'init listFactors timeout',
                );
                const pick = pickFactorForChallenge(fac);
                if (pick) {
                  setMfaChallenge({ factorId: pick.id, factorType: pick.factorType });
                }
              }
            } catch (e) {
              console.warn('Initial MFA step skipped after timeout:', e);
              setMfaChallenge(null);
            }
          } else {
            setMfaChallenge(null);
          }
          setUser(authUser);
          setAccessToken(data.session.access_token);
          persistUserSession(authUser, data.session.access_token);
          authDebug('✅ Session valid, token refreshed');
        } else {
          const storedToken = safeLocalStorageGet('tension_access_token');
          const storedUser = safeLocalStorageGet('tension_user');

          if (storedToken && storedUser) {
            try {
              const payload = JSON.parse(atob(storedToken.split('.')[1]));
              const isExpired = payload.exp && payload.exp < Date.now() / 1000;

              if (isExpired) {
                safeLocalStorageRemove('tension_access_token');
                safeLocalStorageRemove('tension_user');
                sessionStorage.setItem('tension_token_expired', 'true');
                setUser(null);
                setAccessToken(null);
                setMfaChallenge(null);
              } else {
                setAccessToken(storedToken);
                const parsed = JSON.parse(storedUser);
                setUser(normalizeUser(parsed));
              }
            } catch (e) {
              console.error('Invalid token format, clearing session');
              safeLocalStorageRemove('tension_access_token');
              safeLocalStorageRemove('tension_user');
            }
          }
        }
        authDebug('✅ Auth initialization complete');
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        clearTimeout(loadingTimeout);
        setLoading(false);
        authDebug('✅ Auth loading state set to FALSE');
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      authDebug('🔐 Auth state change:', event);

      if (event === 'TOKEN_REFRESHED') {
        authDebug('🔄 Token refreshed automatically');
      }

      if (session) {
        // Session is already set in the client (we're in this callback because it was set).
        // Do NOT call setSession again here—it can hang and block updating React state, so sign-in sticks.
        const authUser = normalizeUser(session.user);
        if (mfaEnforcedRef.current) {
          try {
            const mfa = supabase.auth.mfa;
            const need = await withTimeout(
              needsMfaStep(mfa),
              MFA_API_TIMEOUT_MS,
              'auth state MFA timeout',
            );
            if (need) {
              const { data: fac } = await withTimeout(
                mfa.listFactors(),
                MFA_API_TIMEOUT_MS,
                'auth state listFactors timeout',
              );
              const pick = pickFactorForChallenge(fac);
              if (pick) {
                setMfaChallenge({ factorId: pick.id, factorType: pick.factorType });
              } else {
                setMfaChallenge(null);
              }
            } else {
              setMfaChallenge(null);
            }
          } catch (e) {
            console.warn('onAuthStateChange MFA skipped after timeout:', e);
            setMfaChallenge(null);
          }
        } else {
          setMfaChallenge(null);
        }
        setUser(authUser);
        setAccessToken(session.access_token);
        persistUserSession(authUser, session.access_token);
      } else if (event === 'SIGNED_OUT' || (event as string) === 'USER_DELETED') {
        setUser(null);
        setAccessToken(null);
        setMfaChallenge(null);
        persistUserSession(null, null);
        try {
          sessionStorage.removeItem('tension_token_expired');
        } catch {
          /* ignore */
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const AUTH_FETCH_TIMEOUT_MS = 20000;

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AUTH_FETCH_TIMEOUT_MS);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${publicAnonKey}`,
          },
          body: JSON.stringify({ email, password, name }),
          signal: controller.signal,
        },
      );
      clearTimeout(timeoutId);

      let data: { error?: string };
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid response from server. Please try again.');
      }
      if (!response.ok) {
        throw new Error(data.error || 'Sign up failed');
      }

      authDebug('✅ Sign up successful - auto signing in');
      return await signIn(email, password);
    } catch (error) {
      console.error('Sign up error:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Sign up timed out. Please check your connection and try again.');
      }
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), AUTH_FETCH_TIMEOUT_MS);
      let data: SignInApiResponse | null = null;
      let sessionFromApi = false;
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/signin`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${publicAnonKey}`,
            },
            body: JSON.stringify({ email, password }),
            signal: controller.signal,
          },
        );
        let parsed: SignInApiResponse;
        try {
          parsed = await response.json();
        } catch {
          parsed = {};
        }
        if (!response.ok) {
          throw new Error(parsed.error || `Sign in failed (${response.status})`);
        }
        data = parsed;
        sessionFromApi = true;
      } catch (serverSignInError) {
        console.warn('Primary sign-in endpoint unavailable, using direct Supabase fallback:', serverSignInError);
        const { data: directData, error: directError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (directError) throw directError;
        if (!directData.session) {
          throw new Error('No session created');
        }
        data = {
          session: {
            access_token: directData.session.access_token,
            refresh_token: directData.session.refresh_token,
          },
          user: directData.user,
        };
      }
      clearTimeout(timeoutId);

      const sess = data?.session;
      if (!sess?.access_token || !sess.refresh_token) {
        throw new Error('No session created');
      }

      await withTimeout(
        supabase.auth.setSession({
          access_token: sess.access_token,
          refresh_token: sess.refresh_token,
        }),
        SET_SESSION_TIMEOUT_MS,
        'Saving your session timed out. Please try again.',
      );

      const normalizedUser = normalizeUser(data.user ?? null);
      if (mfaEnforcedAtLogin) {
        const mfa = supabase.auth.mfa;
        const need = await withTimeout(
          needsMfaStep(mfa),
          MFA_API_TIMEOUT_MS,
          'Sign-in security check timed out. Please try again.',
        );
        if (need) {
          const { data: fac, error: facErr } = await withTimeout(
            mfa.listFactors(),
            MFA_API_TIMEOUT_MS,
            'Sign-in security check timed out. Please try again.',
          );
          if (facErr) console.warn('listFactors', facErr);
          const pick = pickFactorForChallenge(fac);
          if (pick) {
            setMfaChallenge({ factorId: pick.id, factorType: pick.factorType });
            setUser(normalizedUser);
            setAccessToken(sess.access_token);
            persistUserSession(normalizedUser, sess.access_token);
            return { mfaPending: true };
          }
        }
      }

      setMfaChallenge(null);
      setUser(normalizedUser);
      setAccessToken(sess.access_token);
      persistUserSession(normalizedUser, sess.access_token);
      void trackTelemetryEvent('auth_signin_succeeded', {
        has_mfa: mfaEnforcedAtLogin,
        signin_path: sessionFromApi ? 'edge_function' : 'direct_supabase_fallback',
      });
      return { mfaPending: false };
    } catch (error) {
      console.error('Sign in error:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Sign in timed out. Please check your connection and try again.');
      }
      throw error;
    }
  };

  const completeMfaTotp = async (code: string) => {
    if (!mfaChallenge || mfaChallenge.factorType !== 'totp') {
      throw new Error('No TOTP challenge active');
    }
    const mfa = supabase.auth.mfa;
    const { data: ch, error: chErr } = await mfa.challenge({ factorId: mfaChallenge.factorId });
    if (chErr || !ch) throw chErr || new Error('MFA challenge failed');
    const { error: vErr } = await mfa.verify({
      factorId: mfaChallenge.factorId,
      challengeId: ch.id,
      code: code.replace(/\s/g, ''),
    });
    if (vErr) throw vErr;
    setMfaChallenge(null);
    await applySessionFromSupabase(setUser, setAccessToken);
  };

  const completeMfaPasskey = async () => {
    if (!mfaChallenge || mfaChallenge.factorType !== 'webauthn') {
      throw new Error('No passkey challenge active');
    }
    const webauthnApi = (
      supabase.auth.mfa as {
        webauthn?: {
          authenticate: (
            args: unknown,
          ) => Promise<{ data: unknown; error: Error | null }>;
        };
      }
    ).webauthn;
    if (webauthnApi?.authenticate) {
      const { error: authErr } = await webauthnApi.authenticate({
        factorId: mfaChallenge.factorId,
        webauthn: {},
      });
      if (authErr) throw authErr;
    } else {
      const mfa = supabase.auth.mfa;
      const rpId = typeof window !== 'undefined' ? window.location.hostname : '';
      const { data: ch, error: chErr } = await mfa.challenge({
        factorId: mfaChallenge.factorId,
        webauthn: {
          rpId: rpId || 'localhost',
          rpOrigins:
            typeof window !== 'undefined' ? [window.location.origin] : undefined,
        },
      });
      if (chErr || !ch) throw chErr || new Error('Passkey challenge failed');
      const raw = ch as Record<string, unknown>;
      const opts =
        (raw.webauthn as Record<string, unknown> | undefined)?.credential_request_options ??
        (raw.webauthn as Record<string, unknown> | undefined)?.credentialRequestOptions ??
        raw.credential_request_options ??
        raw.credentialRequestOptions;
      if (!opts || typeof opts !== 'object') {
        throw new Error(
          'Passkeys require Supabase MFA WebAuthn. Enable it in Auth settings or update the client.',
        );
      }
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const assertion = await startAuthentication({
        optionsJSON: opts as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const { error: vErr } = await mfa.verify({
        factorId: mfaChallenge.factorId,
        challengeId: (ch as { id: string }).id,
        // Supabase expects WebAuthn credential shape; browser assertion matches at runtime
        webauthn: assertion as any,
      });
      if (vErr) throw vErr;
    }
    setMfaChallenge(null);
    await applySessionFromSupabase(setUser, setAccessToken);
  };

  const cancelMfaChallenge = async () => {
    setMfaChallenge(null);
    await supabase.auth.signOut();
    setUser(null);
    setAccessToken(null);
    persistUserSession(null, null);
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${getBaseUrl()}/auth/callback`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Sign in with Google error:', error);
      throw error;
    }
  };

  const signInWithApple = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${getBaseUrl()}/auth/callback`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Sign in with Apple error:', error);
      throw error;
    }
  };

  const signOut = useCallback(async () => {
    setMfaChallenge(null);
    setUser(null);
    setAccessToken(null);
    setAdminAccessTokenForApi(null);
    persistUserSession(null, null);
    try {
      sessionStorage.removeItem('tension_token_expired');
    } catch {
      /* ignore */
    }
    try {
      safeLocalStorageRemove(CLOUD_BOOTSTRAP_DONE_KEY);
    } catch {
      /* ignore */
    }
    clearLoginSnapshotSessionState();
    clearAllConsumerLoginSnapshots();
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) {
        console.error('signOut:', error);
      }
    } catch (e) {
      console.error('signOut failed:', e);
    }
    void trackTelemetryEvent('auth_signout_completed');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        loading,
        mfaChallenge,
        signUp,
        signIn,
        signInWithGoogle,
        signInWithApple,
        signOut,
        completeMfaTotp,
        completeMfaPasskey,
        cancelMfaChallenge,
        syncMfaFromSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
